/// <reference types="react/next" />
/* eslint-disable max-classes-per-file */
/* Copyright (c) 2020-present CloudIO Inc */

import {
  AllDatasources,
  Atom,
  DBRow,
  IoUserPagesV,
  NewRow,
  Query,
  RecordStatus,
} from '@cloudio-saas/datasource-types';
import {
  AsyncCallback,
  Callback,
  emptyFunction,
  notInAContext,
  Path,
  StateXActionCaller,
  StateXCallProps,
  StateXGetter,
  StateXQuiteSetter,
  StateXRemover,
  StateXSetter,
} from '@cloudio-saas/statex';
import { Theme } from '@mui/material';
import {
  AliasAndEmbeddedPageIdValue,
  AliasType,
  AnyItem,
  AnyManager,
  CommonProps,
  defaultCommonProps,
  ExpressionValue,
  getManager,
  Item,
  ItemID,
  ItemType,
  Page,
  PropertyType,
  Resource,
  SendRequest,
} from 'componentregistry';
import { ActionQueryChartItem } from 'componentregistry/actions/ActionQueryChart/ActionQueryChart';
import { ActionQuerySelectItem } from 'componentregistry/actions/ActionQuerySelect/ActionQuerySelect';
import { BaseDataStore } from 'componentregistry/components/DataStore/DataStore';
import {
  AtomQueue,
  DataSourceName,
  Platform,
  ShowMsgItem,
  ShowMsgProps,
} from 'componentregistry/platform/Platform';
import { alertDialogAction, AlertDialogProps } from 'custom/AlertDialog';
import {
  userInputDialogAction,
  UserInputDialogProps,
} from 'custom/UserInputDialog';
import { activeItemSelector } from 'designer/activeItemSelector';
import {
  designPath,
  developerModeAtom,
  embeddedPagesAtom,
  formulaPropPath,
  itemAtom,
  itemPropPath,
  pageAtom,
  runtimeItemAtom,
} from 'designer/atoms';
import type { Location } from 'history';
import stringify from 'json-stable-stringify';
import {
  activePinnedSidebarPanelAtom,
  activeSidebarPanelAtom,
  treeViewFilterAtom,
} from 'layout/Sidebar/atoms';
import { replaceItemPropertyValue } from 'layout/Sidebar/panels/PropertiesView/property-editors/PropertyEditorUtils';
import { TreeTypeDesigner } from 'layout/Sidebar/panels/TreeView/state';
import { getShortenedId, updateItemIDWithinPage } from 'platform/PlatformUtils';
import { PLATFORM } from 'platform/symbols';
import { SetStateAction, startTransition } from 'react';
import { NavigateFunction } from 'react-router-dom';
import IOResource from 'root/Resource';
import { getUserName, isDeveloper, userAtom } from 'root/useUser';
import { routeAtom } from 'routes/RouteState';
import Store, { initialState } from 'store/Store';
import { getStore } from 'store/useDataStore';
import { WebSocketProps } from 'streams/useWebSocket';
import { ulid } from 'ulid';
import assert from 'utils/assert';
import { isEmpty, isNotEmpty, isNull, NOT_DEFINED } from 'utils/isEmpty';
import Logger from 'utils/Logger';
import GlobalPubSub, { PubSub } from 'utils/PubSub';
import {
  startPageProgress,
  startProgressTracking,
} from '../components/Page/PageProgressed';
import { getUrl } from '../config/config';
import {
  evalBoolean,
  evalNumber,
  evalString,
  evalUnknown,
} from '../expression/useExpression';
import { loadingSignal } from '../layout/Header/LoadingIndicator';
import {
  recordedUIMessagesAtom,
  recordUIMessagesAtom,
  recordUIMessagesWithWaitAtom,
} from '../layout/Sidebar/panels/TestsView/state';
import { getURLParam, getURLQueryStringToPost } from '../utils/url';
import { getDesignProp, getItemProp, getRuntimeProp } from './GetProp';
import { Note, notificationAtom } from './NotificationItem';
import { SQLType } from './SQL';
import { localStorageSelector } from 'expression/LocalStorage';
import { focusItemIdAtom } from 'hooks/useFocus';
import { ActionQueryCalendarItem } from 'componentregistry/actions/ActionQueryCalendar/ActionQueryCalendar';
import { processChildActions } from 'triggers/Triggers';
// import AbortError from './AbortError';

const EMPTY_ARRAY: never[] = [];
const dsAliasCache = new Map<string, Map<string, string>>();

let _resource: Resource | null = null;

export function makeSingleInsertPost(platform: Platform, _rs: RecordStatus) {
  return async function insertOne<T extends DataSourceName>(
    appUid: string,
    ds: T,
    row: NewRow<AllDatasources[T]>,
  ): Promise<DBRow<AllDatasources[T]>> {
    const [promise] = platform.sendRequest(
      {
        type: 'Post',
        data: { testAlias: { ds, data: [{ ...row, _rs }] } },
      },
      { appUid, test: true },
    );
    const response = await promise;
    if (
      response.status === 'success' &&
      response.data.testAlias.data.length === 1
    ) {
      return response.data.testAlias.data[0];
    } else {
      console.error('Unexpected response. Expected 1 row.');
      console.error(response);
      throw new Error('Unexpected response!');
    }
  };
}

export function makeSinglePost(platform: Platform, _rs: RecordStatus) {
  return async function updateOne<T extends DataSourceName>(
    appUid: string,
    ds: T,
    row: DBRow<AllDatasources[T]>,
  ): Promise<DBRow<AllDatasources[T]>> {
    const [promise] = platform.sendRequest(
      {
        type: 'Post',
        data: { testAlias: { ds, data: [{ ...row, _rs }] } },
      },
      { appUid, test: true },
    );
    const response = await promise;
    if (
      response.status === 'success' &&
      response.data.testAlias.data.length === 1
    ) {
      return response.data.testAlias.data[0];
    } else {
      console.error('Unexpected response. Expected 1 row.');
      console.error(response);
      throw new Error('Unexpected response!');
    }
  };
}

export function makeMultiInsertPost(platform: Platform, _rs: RecordStatus) {
  return async function insertMany<T extends DataSourceName>(
    appUid: string,
    ds: T,
    rows: NewRow<AllDatasources[T]>[],
  ): Promise<DBRow<AllDatasources[T]>[]> {
    const [promise] = platform.sendRequest(
      {
        type: 'Post',
        data: {
          testAlias: { ds, data: rows.map((row) => ({ ...row, _rs })) },
        },
      },
      { appUid, test: true },
    );
    const response = await promise;
    return response.status === 'success' ? response.data.testAlias.data : [];
  };
}

export function makeMultiPost(platform: Platform, _rs: RecordStatus) {
  return async function insertMany<T extends DataSourceName>(
    appUid: string,
    ds: T,
    rows: DBRow<AllDatasources[T]>[],
  ): Promise<DBRow<AllDatasources[T]>[]> {
    const [promise] = platform.sendRequest(
      {
        type: 'Post',
        data: {
          testAlias: { ds, data: rows.map((row) => ({ ...row, _rs })) },
        },
      },
      { appUid, test: true },
    );
    const response = await promise;
    return response.status === 'success' ? response.data.testAlias.data : [];
  };
}

export function makeFind(platform: Platform) {
  return async function find<T extends DataSourceName>(
    appUid: string,
    ds: T,
    query: Query<AllDatasources[T]>,
  ): Promise<DBRow<AllDatasources[T]>[]> {
    const [promise] = platform.sendRequest(
      {
        type: 'Post',
        data: {
          testAlias: { ds, query },
        },
      },
      { appUid, test: true },
    );
    const response = await promise;
    return response.status === 'success' ? response.data.testAlias.data : [];
  };
}
let counter = 0;
class IOPlatform implements Platform {
  id: number;

  $$typeof = PLATFORM;

  theme: Theme;

  pubSub: PubSub | null = null;

  caller: StateXActionCaller;

  getter: StateXGetter;

  remover: StateXRemover;

  setter: StateXSetter;

  setQuietly: StateXQuiteSetter;

  quietCallback: <R>(fn: (_props: StateXCallProps) => R) => R;

  isStateXMounted: () => boolean;

  sendRequest: SendRequest = notInAContext;

  callFetch: (
    appUid: string,
    sql: string,
    update?: boolean | undefined,
  ) => Promise<SQLType> = notInAContext;

  ws: WebSocketProps = {
    addListener: notInAContext,
    addReconnectListener: notInAContext,
    close: notInAContext,
    doConnect: notInAContext,
    hashRef: { current: '' },
    isSocketOpen: notInAContext,
    readyState: notInAContext,
    sendRequest: notInAContext,
    startBatch: notInAContext,
    executeBatch: notInAContext,
    addToBatch: notInAContext,
    isIdle: notInAContext,
  };

  addToUndo: (childPath: Path, hash: string) => void;

  navLoc?: {
    location: Location;
    navigate: NavigateFunction;
  };

  postUpdateCallbackRegister: (c: Callback) => void;

  forceUpdater: <T = void>(callback: Callback<T>) => T;

  stateXScheduler: () => void;

  asyncBatchUpdater: <T = void>(callback: AsyncCallback<T>) => Promise<T>;

  asyncForceUpdater: <T = void>(callback: AsyncCallback<T>) => Promise<T>;

  batchUpdater: <T = void>(callback: Callback<T>) => T;

  _originalPageId: string;

  _appUid: string;

  _pageId: string;

  _roleUid: string;

  atomQueue = new AtomQueue();

  _atomCounter = 0;

  _pages: DBRow<IoUserPagesV>[] = [];

  constructor(
    appUid: string,
    roleUid: string,
    pageId: string,
    setQuietly: StateXQuiteSetter,
    set: StateXSetter,
    get: StateXGetter,
    call: StateXActionCaller,
    remove: StateXRemover,
    addToUndo: (childPath: Path, hash: string) => void,
    postUpdateCallbackRegister: (c: Callback) => void,
    forceUpdater: <T = void>(callback: Callback<T>) => T,
    asyncBatchUpdater: <T = void>(callback: AsyncCallback<T>) => Promise<T>,
    asyncForceUpdater: <T = void>(callback: AsyncCallback<T>) => Promise<T>,
    batchUpdater: <T = void>(callback: Callback<T>) => T,
    stateXScheduler: () => void,
    quietCallback: <R>(fn: (_props: StateXCallProps) => R) => R,
    isStateXMounted: () => boolean,
    pages: DBRow<IoUserPagesV>[],
  ) {
    this.id = ++counter;
    this._appUid = appUid;
    this._pageId = pageId;
    this._roleUid = roleUid;
    this._originalPageId = pageId;
    this.setQuietly = setQuietly;
    this.caller = call;
    this.getter = get;
    this.remover = remove;
    this.setter = set;
    this.addToUndo = addToUndo;
    this.postUpdateCallbackRegister = postUpdateCallbackRegister;
    this.forceUpdater = forceUpdater;
    this.asyncBatchUpdater = asyncBatchUpdater;
    this.asyncForceUpdater = asyncForceUpdater;
    this.batchUpdater = batchUpdater;
    this.theme = {} as unknown as Theme; // will be set by ThemeUpdater
    this.stateXScheduler = stateXScheduler;
    this.quietCallback = quietCallback;
    this.isStateXMounted = isStateXMounted;
    this._pages = pages;
  }

  setPages(pages: DBRow<IoUserPagesV>[]) {
    this._pages = pages;
  }

  getGetter() {
    return this.getter;
  }

  showMessage = (props: ShowMsgProps): string => {
    const { msg, type, item } = props;
    if (Logger.isLogEnabled()) {
      Logger.log({
        itemType: 'ShowMessage',
        status:
          type === 'warning'
            ? 'warn'
            : type === 'success'
            ? 'info'
            : type ?? 'debug',
        msg,
        itemId: item?.itemId,
      });
    }
    if (this.isRecording()) {
      this.recordLine('', `await ui.hasNotified((msg) => msg === \`${msg}\`);`);
    }
    const id = ulid();
    let { action, timeoutMs } = props;
    const notifications = this.getter(notificationAtom);
    if (notifications.length) {
      const last = notifications.length - 1;
      // just increment the count instead of adding a new entry
      // if the msg matches any of the recent 5 notification msgs
      for (let i = last; i >= 0 && i >= last - 5; i--) {
        const previousNotification = notifications[i];
        if (
          previousNotification.msg === msg &&
          previousNotification.type === type
        ) {
          const newNotification: Note = {
            ...previousNotification,
            count: (previousNotification.count ?? 1) + 1,
            duplicateIds: [...previousNotification.duplicateIds, id],
          };
          this.setter(notificationAtom, (n) => {
            const others = [...n];
            others.splice(i, 1);
            return [...others, newNotification];
          });
          return id;
        }
      }
    }
    if (item && !action && isDeveloper(this, item.appUid)) {
      action = {
        label: 'Show Item',
        callback: () => {
          this.setter(treeViewFilterAtom, '', {
            params: { treename: 'designer' },
          });
          this.selectItemInDesigner(item);
        },
      };
      timeoutMs = -1;
    }
    this.setter(notificationAtom, (n) => [
      ...n,
      { id, msg, type, timeoutMs, action, duplicateIds: [] },
    ]);
    return id;
  };

  removeNotification = (id: string) => {
    const notifications = this.getter(notificationAtom);
    if (notifications.length) {
      const idx = notifications.findIndex(
        (n) => n.id === id || n.duplicateIds.includes(id),
      );
      if (idx !== -1) {
        const note = { ...notifications[idx] };
        this.setter(notificationAtom, (n) => {
          const others = [...n];
          others.splice(idx, 1);
          if (note.duplicateIds.includes(id) && note.count) {
            note.count -= 1;
            return [...others, note];
          } else {
            return [...others];
          }
        });
      }
    }
  };

  selectItemInDesigner = (item: ShowMsgItem) => {
    this.setter(developerModeAtom, true, {
      params: { appUid: item.appUid },
    });
    this.setter(activePinnedSidebarPanelAtom, 'props', {
      params: { userName: getUserName(this) },
    });
    this.setter(activeSidebarPanelAtom, 'tree');
    setTimeout(() => {
      this.setter(
        activeItemSelector,
        {
          ...item,
          expand: true,
          treename: TreeTypeDesigner,
        },
        {
          params: {
            appUid: item.appUid,
            pageId: item.pageId,
            treename: TreeTypeDesigner,
          },
          props: this,
        },
      );
    }, 1000);
  };

  showError = (msg: string | Error, item?: ShowMsgItem) => {
    let message = '';
    if (typeof msg !== 'string') {
      message =
        msg.message ||
        'Unknown error!. Check the console log for more details.';
      if (console) console.error(msg);
    } else {
      message = msg;
    }
    this.showMessage({ msg: message, type: 'error', item });
  };

  showWarning = (msg: string, item?: ShowMsgItem): string => {
    return this.showMessage({ msg, type: 'warning', item });
  };

  showInfo = (msg: string) => {
    this.showMessage({ msg, type: 'info' });
  };

  showSuccess = (msg: string) => {
    this.showMessage({ msg, type: 'success' });
  };

  confirm = (title: string, content: string): Promise<boolean> => {
    GlobalPubSub.pub('NeedUserConfirmation', { title, content }, true, true);
    return new Promise((resolve) => {
      this.confirmDialog({
        title,
        content,
        onOk: () => {
          resolve(true);
        },
        onClose: () => {
          resolve(false);
        },
      });
    });
  };

  confirmDialog = (props: AlertDialogProps): void => {
    this.caller(alertDialogAction, props);
  };

  userInput = (
    props: Omit<UserInputDialogProps, 'onClose' | 'onOk'>,
  ): Promise<string> => {
    return new Promise((resolve, reject) => {
      this.userInputDialog({
        ...props,
        onOk: (value) => {
          resolve(value);
        },
        onClose: () => {
          reject('Cancelled');
        },
      });
    });
  };

  userInputDialog = (props: UserInputDialogProps): void => {
    this.caller(userInputDialogAction, props);
  };

  get pageId() {
    if (this._pageId === '$') {
      return this.getter(routeAtom).page || this._pageId;
    }
    return this._pageId;
  }

  get roleUid() {
    if (this._pageId === '$') {
      return this.getter(routeAtom).role || this._roleUid;
    }
    return this._roleUid;
  }

  set roleUid(_roleUid: string) {
    this._roleUid = _roleUid;
  }

  get originalPageId() {
    return this._originalPageId;
  }

  get appUid() {
    if (this._pageId === '$') {
      return this.getter(routeAtom).app || this._appUid;
    }
    return this._appUid;
  }

  getManager(itemId: string): AnyManager | null {
    if (!itemId) return null;
    const item = this.getter(itemAtom, {
      params: { appUid: this.appUid, pageId: this.pageId, itemId },
    });
    if (!item) {
      // this.showError(`Invalid item ${itemId}`);
      return null;
    }
    return getManager(item.type);
  }

  getRuntimeProp = <T, K extends keyof T = keyof T>(
    appUid: string,
    pageId: ItemID,
    itemId: ItemID,
    key: K,
  ): T[K] => {
    return getRuntimeProp<T, K>(this.getter, appUid, pageId, itemId, key);
  };

  getItemProp = <T, K extends keyof T = keyof T>(
    appUid: string,
    pageId: ItemID,
    itemId: ItemID,
    key: K,
  ): T[K] => {
    return getItemProp<T, K>(this.getter, appUid, pageId, itemId, key);
  };

  getDesignProp = <T, K extends keyof T = keyof T>(
    appUid: string,
    pageId: ItemID,
    itemId: ItemID,
    key: K,
  ): T[K] => {
    return getDesignProp<T, K>(this.getter, appUid, pageId, itemId, key);
  };

  redrawCanvasGrid = (appUid: string, pageId: ItemID, itemId: ItemID): void => {
    this.setRuntimeProp(appUid, pageId, itemId, 'redraw', (v = 0) => v + 1);
  };

  setRuntimeProp = <T>(
    appUid: string,
    pageId: ItemID,
    itemId: ItemID,
    key: keyof T,
    value: SetStateAction<T[keyof T]>,
  ) => {
    if (pageId === '$sidebar$' || pageId.startsWith('$$embeDDed$$')) {
      // ignore for sidebar
    } else {
      const item = this.getItem(appUid, pageId, itemId);
      if (!item) {
        this.showError(
          `Missing item with ID ${itemId} in page ${appUid}.${pageId}!`,
        );
        return;
      }
      if (
        item.type === 'TabPanel' &&
        key === 'activeTab' &&
        (item as Item<'TabPanel'>).props.updateURL &&
        typeof value === 'string'
      ) {
        const params = new URLSearchParams(this.location.search);
        const _p = params.get('_p');
        const p = _p ? JSON.parse(atob(_p)) : {};
        p[`t_${itemId}`] = value;
        params.set('_p', btoa(JSON.stringify(p)));
        this.location.search = params.toString();
        if (Logger.isLogEnabled()) {
          Logger.log({
            itemType: 'activeTab',
            itemId,
            status: 'debug',
            msg: `Updating URL with activeTab ${
              this.location.pathname
            } with ${`t_${itemId}`}=${value}`,
          });
        }
        this.navigate(this.location, { replace: true });
      }
    }
    this.setter(
      runtimeItemAtom,
      (r) => {
        if (typeof value === 'function') {
          // @ts-ignore value is a SetStateAction function
          const v = value(r[key]);
          return { ...r, [key]: v };
        }
        if (Logger.isLogEnabled()) {
          Logger.log({
            itemType: 'Set-Runtime-Prop',
            itemId,
            status: 'debug',
            msg:
              value instanceof HTMLElement
                ? value.tagName
                : `${JSON.stringify({
                    [key]: value,
                  })}`,
          });
        }
        return { ...r, [key]: value };
      },
      {
        params: { appUid, pageId, itemId },
      },
    );
  };

  setDesignProp = <T>(
    appUid: string,
    pageId: ItemID,
    itemId: ItemID,
    key: keyof T,
    value: SetStateAction<T[keyof T]>,
  ) => {
    this.setter(
      itemPropPath,
      (oldValue: T) => {
        if (typeof value === 'function') {
          // @ts-ignore value is a SetStateAction function
          return value(oldValue);
        }
        return value;
      },
      {
        params: { appUid, pageId, itemId, prop: key as string },
      },
    );
  };

  setFormulaProp = <T>(
    appUid: string,
    pageId: ItemID,
    itemId: ItemID,
    key: keyof T,
    value: SetStateAction<T[keyof T]>,
  ) => {
    this.setter(
      formulaPropPath,
      (oldValue: T) => {
        if (typeof value === 'function') {
          // @ts-ignore value is a SetStateAction function
          return value(oldValue);
        }
        return value;
      },
      {
        params: { appUid, pageId, itemId, prop: key as string },
      },
    );
  };

  replacePropertyValue = (
    page: Page,
    appUid: string,
    pageId: string,
    propertyType: PropertyType,
    oldValue: unknown,
    newValue: unknown,
  ): Page => {
    const itemIds = Object.keys(page);
    itemIds.forEach((itemId) => {
      const _item = page[itemId];
      if (!_item.type) {
        console.warn(`Empty item ${itemId}`);
        return;
      }
      const mgr = getManager(_item.type);
      page[itemId] = replaceItemPropertyValue({
        platform: this,
        appUid,
        page,
        pageId,
        propertyType,
        oldValue,
        newValue,
        item: _item,
        mgr,
      });
    });
    return page;
  };

  shortenItemID = (
    appUid: string,
    pageId: ItemID,
    itemId: ItemID,
    donotUpdateActiveItem?: boolean,
  ): string => {
    const newId = this.getShortenedId(appUid, pageId, itemId);
    const err = this.updateItemID(
      appUid,
      pageId,
      itemId,
      newId,
      donotUpdateActiveItem,
    );
    return err ? itemId : newId;
  };

  getShortenedId = (appUid: string, pageId: ItemID, itemId: ItemID): string => {
    const page = this.getter(pageAtom, { params: { appUid, pageId } });
    if (!page?.[itemId]) return '';
    return getShortenedId({ page, itemId, platform: this, pageId, appUid });
  };

  updateItemID = (
    appUid: string,
    pageId: ItemID,
    oldItemId: ItemID,
    newItemId: ItemID,
    donotUpdateActiveItem?: boolean,
  ): string => {
    const page = this.getter(pageAtom, { params: { appUid, pageId } });
    if (!page) {
      throw Error(`Invalid page w/ id ${pageId}`);
    }
    if (oldItemId === 'root' || newItemId === 'root') {
      return 'root is a reserved word for ItemID!';
    }
    const itm = page[newItemId];
    if (itm) {
      return `Item of type ${itm.type} already exists with itemId: ${newItemId}!`;
    }

    const p = updateItemIDWithinPage(
      this,
      page,
      appUid,
      pageId,
      oldItemId,
      newItemId,
    );

    this.addToUndo(designPath({ appUid, pageId }), pageId);
    this.setter(pageAtom, p, { params: { appUid, pageId } });
    if (!donotUpdateActiveItem) {
      this.setter(
        activeItemSelector,
        {
          appUid,
          itemId: newItemId,
          pageId,
          expand: true,
          treename: 'designer' as const,
        },
        { params: { treename: 'designer', pageId, appUid }, props: this },
      );
    }
    this.forceUpdater(emptyFunction);
    return '';
  };

  getChildren(appUid: string, pageId: ItemID, itemId: ItemID): ItemID[] {
    return (
      this.getter(designPath({ appUid, pageId, itemId, key: 'children' })) ??
      EMPTY_ARRAY
    );
  }

  getItem(appUid: string, pageId: ItemID, itemId: ItemID): AnyItem | undefined {
    return this.getter(designPath({ appUid, pageId, itemId }));
  }

  getPage(appUid: string, pageId: ItemID): Page {
    return this.getter(designPath({ appUid, pageId }));
  }

  isDeveloperMode(pageId: ItemID): boolean {
    if (
      pageId &&
      pageId !== `${this.appUid}.home` &&
      this.getter(['db', 'IoPages', pageId, 'appUid']) !== this.appUid
    ) {
      return false;
    }
    const devMode = this.getter(developerModeAtom, {
      params: { appUid: this.appUid },
    });
    const embeddedPage = this.getter(embeddedPagesAtom).includes(pageId);
    // console.log({ pageId, devMode, embeddedPage });
    return devMode && !embeddedPage;
  }

  hasChildrenOfTypeById<T extends ItemType>(
    appUid: string,
    pageId: ItemID,
    itemId: ItemID,
    type: T,
  ): boolean {
    const page = this.getPage(appUid, pageId);
    if (!page) {
      this.showError(
        `Page ${pageId} not found! in call to hasChildrenOfTypeById with itemId ${itemId} && type ${type}.`,
      );
      return false;
    }
    const item = page[itemId];
    if (!item) {
      this.showError(`Item ${itemId} not found in page ${pageId}!`);
      return false;
    }
    return (
      this._getChildrenItems(page, item, (c) => c.type === type).length > 0
    );
  }

  getChildrenOfTypeById<T extends ItemType>(
    appUid: string,
    pageId: ItemID,
    itemId: ItemID,
    type: T,
  ): Item<T>[] {
    const page = this.getPage(appUid, pageId);
    if (!page) {
      if (pageId !== 'x') {
        this.showError(
          `Page ${pageId} not found! in call to getChildrenOfTypeById with itemId ${itemId} && type ${type}.`,
        );
      }
      return [];
    }

    const item = page[itemId];
    if (!item) {
      this.showError(`Item ${itemId} not found in page!.`);
      return [];
    }
    return this.getChildrenOfType(appUid, pageId, page, item, type);
  }

  _getChildrenItems = <R extends AnyItem>(
    page: Page,
    item: AnyItem,
    filter: (child: AnyItem) => boolean,
  ): R[] => {
    return item.children.map((c) => page[c]).filter(filter) as R[];
  };

  getChildrenItems = <R extends AnyItem>(
    appUid: string,
    pageId: ItemID,
    page: Page,
    item: AnyItem,
    filter: (child: AnyItem) => boolean,
  ): R[] => {
    return this._getChildrenItems(page, item, filter).map((c) => {
      const mgr = getManager(c.type);
      const runtimeItem = this.getter(runtimeItemAtom, {
        params: { appUid, pageId, itemId: c.itemId },
      });
      const _props: Record<string, unknown> = {
        ...defaultCommonProps,
        ...mgr.defaultValue,
      };
      Object.keys(_props).forEach((key) => {
        // @ts-ignore c.props
        _props[key] = runtimeItem[key] ?? c.props[key] ?? _props[key];
      });
      // Changed to Object.assign instead of object spread to avoid
      // TS2590: Expression produces a union type that is too complex to represent
      // eslint-disable-next-line prefer-object-spread
      // const props = Object.assign(
      //   {},
      //   defaultCommonProps,
      //   mgr.defaultValue,
      //   c.props,
      //   runtimeItem,
      // ) as CommonProps;
      const props = _props as CommonProps;
      return { ...c, props };
    }) as R[];
  };

  getChildrenItemsById<R extends AnyItem>(
    appUid: string,
    pageId: ItemID,
    itemId: ItemID,
    filter: (child: AnyItem) => boolean,
  ): R[] {
    const page = this.getPage(appUid, pageId);
    if (!page) {
      this.showError(`Page ${pageId} not found!`);
      return [];
    }
    return this.getChildrenItems(
      appUid,
      pageId,
      page,
      page[itemId],
      filter,
    ) as R[];
  }

  _collectDataSourceItems(
    page: Page,
    children: string[],
    collection: AnyItem[],
  ) {
    for (let i = 0; i < children.length; i++) {
      const id = children[i];
      const itm = page[id];
      if (itm) {
        if (
          itm.type === 'DataStore' ||
          itm.type === 'TransientDataStore' ||
          itm.type === 'LocalDataStore' ||
          itm.type === 'CloudDataStore' ||
          itm.type === 'ReadOnlyDataStore'
        ) {
          collection.push(itm);
        }
        if (itm.type === 'Folder') {
          this._collectDataSourceItems(page, itm.children, collection);
        }
      }
    }
  }

  collectAllDataStoreItems = <R extends AnyItem>(
    appUid: string,
    pageId: ItemID,
  ): R[] => {
    const page = this.getPage(appUid, pageId);
    const collection: AnyItem[] = [];
    this._collectDataSourceItems(page, page.root.children, collection);
    return collection.map((c) => {
      const mgr = getManager(c.type);
      const runtimeItem = this.getter(runtimeItemAtom, {
        params: { appUid, pageId, itemId: c.itemId },
      });
      const _props: Record<string, unknown> = {
        ...defaultCommonProps,
        ...mgr.defaultValue,
      };
      Object.keys(_props).forEach((key) => {
        // @ts-ignore c.props
        _props[key] = runtimeItem[key] ?? c.props[key] ?? _props[key];
      });
      const props = _props as CommonProps;
      return { ...c, props };
    }) as R[];
  };

  findParent(
    appUid: string,
    pageId: ItemID,
    itemId: ItemID,
    filter: (parent: AnyItem) => boolean,
  ): AnyItem | null {
    const page = this.getPage(appUid, pageId);
    let parent = page?.[page?.[itemId]?.parent];
    while (parent) {
      // If reach to root return null
      if (parent.itemId === 'root') {
        return null;
      }
      if (filter(parent)) {
        return parent;
      }
      parent = page[parent.parent];
    }
    return null;
  }

  hasChild(
    page: Page,
    itemId: ItemID,
    predicate: (parent: AnyItem) => boolean,
  ): boolean {
    if (!page) {
      this.showError(
        'Empty page passed to call hasChild with itemId $(itemId).',
      );
      return false;
    }
    const children = page[itemId]?.children;
    if (children?.length) {
      const found = children.find((child) => {
        const item = page[child];
        if (item && predicate(item)) {
          return true;
        }
        return this.hasChild(page, child, predicate);
      });
      return !!found;
    }
    return false;
  }

  findParentOfType<T extends ItemType>(
    appUid: string,
    pageId: ItemID,
    itemId: ItemID,
    parentType: T,
  ): Item<T> | null {
    return this.findParent(
      appUid,
      pageId,
      itemId,
      (parent) => parent.type === parentType,
    ) as Item<T> | null;
  }

  getChildrenOfType = <T extends ItemType>(
    appUid: string,
    pageId: ItemID,
    page: Page,
    item: AnyItem,
    type: T,
  ): Item<T>[] => {
    return this.getChildrenItems(
      appUid,
      pageId,
      page,
      item,
      (c) => c.type === type,
    ) as Item<T>[];
  };

  getStore = <S>(
    appUid: string,
    pageId: ItemID,
    alias: string,
    silent = false,
  ): Store<S> | null => {
    if (isEmpty(alias)) return null;
    const dataSource = this.getDataSourceName(appUid, pageId, alias);
    const msg = `Missing store with alias '${alias}' in page '${appUid}.${pageId}'!`;
    if (!dataSource) {
      if (!silent) {
        this.showError(msg);
      }
      return null;
    }
    const store = getStore<S>({ appUid, pageId, ds: dataSource, alias });
    if (!(store || silent)) {
      this.showError(msg);
    }
    return store;
  };

  getParentStore = <S>(
    appUid: string,
    pageId: ItemID,
    itemId: ItemID,
  ): Store<S> | null => {
    const parent = this.findParent(appUid, pageId, itemId, (p) =>
      [
        'DataStoreReference',
        'LOVColumn',
        'LOVField',
        'MutliSelectLOVField',
      ].includes(p.type),
    ) as Item<
      'DataStoreReference' | 'LOVColumn' | 'LOVField' | 'MutliSelectLOVField'
    > | null;

    if (!parent) {
      return null;
    }
    const alias =
      parent.type === 'DataStoreReference'
        ? (parent as Item<'DataStoreReference'>).props.alias
        : (parent as Item<'LOVColumn' | 'LOVField'>).props.comboDatasourceAlias;

    if (isEmpty(alias)) return null;
    const dataSource = this.getDataSourceName(appUid, pageId, alias);
    const msg = `(getParentStore(${itemId})) Missing store with alias ${alias} in page '${appUid}.${pageId}'!`;
    if (!dataSource) {
      this.showError(msg);
      return null;
    }
    const store = getStore<S>({
      appUid,
      pageId,
      ds: dataSource,
      alias: alias,
    });
    if (!store) {
      this.showError(msg);
    }
    return store;
  };

  evalString = (
    appUid: string,
    pageId: ItemID,
    itemId: ItemID,
    property: string,
    props: Record<string, unknown>,
    expression?: string,
  ): string | null => {
    return evalString(
      this,
      {
        appUid,
        pageId,
        itemId,
        property,
        data: props,
        dsAlias: (props.dsAlias as string) ?? NOT_DEFINED,
        rowIndex: (props.rowIndex as number) ?? -1,
        recordId: props.recordId as string,
      },
      expression,
    );
  };

  evalIfExpressionString = (
    appUid: string,
    pageId: ItemID,
    itemId: ItemID,
    property: string,
    props: Record<string, unknown>,
    expression?: ExpressionValue,
  ): string | null => {
    if (typeof expression === 'string') {
      expression = { type: 'text', value: expression };
    }
    if (!expression || expression.type === 'text') {
      return expression?.value ?? null;
    }
    return evalString(
      this,
      {
        appUid,
        pageId,
        itemId,
        property,
        data: props,
        dsAlias: (props.dsAlias as string) ?? NOT_DEFINED,
        rowIndex: (props.rowIndex as number) ?? -1,
        recordId: props.recordId as string,
      },
      expression.value,
    );
  };

  evalBoolean = (
    appUid: string,
    pageId: ItemID,
    itemId: ItemID,
    property: string,
    props: Record<string, unknown>,
    expression?: string,
  ): boolean => {
    return evalBoolean(
      this,
      {
        appUid,
        pageId,
        itemId,
        property,
        data: props,
        dsAlias: (props.dsAlias as string) ?? NOT_DEFINED,
        rowIndex: (props.rowIndex as number) ?? -1,
        recordId: props.recordId as string,
      },
      expression,
    );
  };

  evalNumber = (
    appUid: string,
    pageId: ItemID,
    itemId: ItemID,
    property: string,
    props: Record<string, unknown>,
    expression?: string,
  ): number | null => {
    return evalNumber(
      this,
      {
        appUid,
        pageId,
        itemId,
        property,
        data: props,
        dsAlias: (props.dsAlias as string) ?? NOT_DEFINED,
        rowIndex: (props.rowIndex as number) ?? -1,
        recordId: props.recordId as string,
      },
      expression,
    );
  };

  evalUnknown = (
    appUid: string,
    pageId: ItemID,
    itemId: ItemID,
    property: string,
    props: Record<string, unknown>,
    expression?: string,
  ): unknown => {
    return evalUnknown(
      this,
      {
        appUid,
        pageId,
        itemId,
        property,
        data: props,
        dsAlias: (props.dsAlias as string) ?? NOT_DEFINED,
        rowIndex: (props.rowIndex as number) ?? -1,
        recordId: props.recordId as string,
      },
      expression,
    );
  };

  registerAlias = ({
    appUid,
    pageId,
    alias,
    dataSource,
  }: {
    appUid: string;
    pageId: string;
    alias: string;
    dataSource: string;
  }) => {
    assert(!!dataSource, 'Missing dataSource');
    assert(!!alias, 'Missing alias');
    let map = dsAliasCache.get(`${pageId}-${appUid}`);
    if (!map) {
      map = new Map();
      dsAliasCache.set(`${pageId}-${appUid}`, map);
    }
    map.set(alias, dataSource);
  };

  isAliasRegistered = ({
    appUid,
    pageId,
    alias,
  }: {
    appUid: string;
    pageId: string;
    alias: string;
  }): boolean => {
    assert(!!alias, 'Missing alias');
    const map = dsAliasCache.get(`${pageId}-${appUid}`);
    if (!map) {
      return false;
    }
    return map.has(alias);
  };

  resetAliases = (appUid: string, pageId: string): void => {
    const stores = this.collectAllDataStoreItems(appUid, pageId);
    const map = dsAliasCache.get(`${pageId}-${appUid}`);
    if (map) {
      map.clear();
    }
    stores.forEach((s) => {
      const { alias, dataSource } = s.props as BaseDataStore;
      if (alias && dataSource) {
        this.registerAlias({ appUid, pageId, alias, dataSource });
      }
    });
  };

  getDataSourceName = (appUid: string, pageId: string, alias: string) =>
    dsAliasCache.get(`${pageId}-${appUid}`)?.get(alias);

  getDataSources = (appUid: string, pageId: string) =>
    dsAliasCache.get(`${pageId}-${appUid}`);

  getAllAliases = (appUid: string, pageId: string): string[] =>
    Object.keys(dsAliasCache.get(`${pageId}-${appUid}`) ?? {});

  resource = (): Resource => {
    if (!_resource) {
      _resource = new IOResource(this);
    }
    return _resource;
  };

  signout = () => {
    this.setter([], initialState);
    Store.destroy();
    if (_resource) {
      _resource.destroy();
      _resource = null;
    }
  };

  get navigate(): NavigateFunction {
    const h = this.navLoc?.navigate;
    if (!h) {
      throw Error('Missing platform.history!');
    }
    return h;
  }

  get location(): Location {
    const h = this.navLoc?.location;
    if (!h) {
      throw Error('Missing platform.location!');
    }
    return h;
  }

  goToPage = (
    appUid: string,
    pageId?: string | null,
    params?: string,
  ): boolean => {
    let path = `/${appUid}`;
    if (pageId /* && pageId !== `${appUid}.home` */) {
      path = `/${appUid}/${pageId}`;
    }
    if (!pageId) {
      pageId = `${appUid}.home`;
    }
    const pages = this._pages.filter(
      (p) => p.pageUid === pageId && p.appUid === appUid,
    );
    if (pageId !== `${appUid}.home` && pages.length === 0) {
      // this.showWarning(`You don't have access to page ${appUid}/${pageId}!`);
      // throw new AbortError();
    }

    const searchParams = new URLSearchParams(params);
    const paramsObject: Record<string, unknown> = {};
    const _p = searchParams.get('_p');
    if (_p) {
      const p = JSON.parse(atob(_p));
      Object.assign(paramsObject, p);
      searchParams.delete('_p');
    }
    const urlParams = new URLSearchParams();
    for (const [key, value] of searchParams.entries()) {
      if (key === 'x' || key.startsWith('_')) {
        urlParams.set(key, value);
      } else {
        paramsObject[key] = value;
      }
    }
    let role = paramsObject.role;
    if (!role) {
      role = this.getter(routeAtom).role;
    }
    if (!role) {
      if (pages.length === 0) {
        // home page
        const firstPage = this._pages.find((p) => p.appUid === appUid);
        if (!firstPage) {
          this.showWarning(
            `You don't have access to any page in app ${appUid}! Please contact your administrator.`,
          );
        }
        role = firstPage?.roleUid || 'all_users';
      }
    }
    if (pages.length && !(role && pages.find((p) => p.roleUid === role))) {
      role = pages[0].roleUid;
    }
    paramsObject.role = role;

    const pstring = btoa(stringify(paramsObject));
    urlParams.set('_p', pstring);

    path = `${path}?${urlParams.toString()}`;

    const { pathname, search } = this.location;
    const currentParams = new URLSearchParams(search);
    currentParams.delete('x');

    if (`${pathname}?${currentParams.toString()}` !== path) {
      if (Logger.isLogEnabled()) {
        Logger.log({
          itemType: 'goToPage',
          status: 'debug',
          msg: `GoToPage ${path}`,
        });
      }
      this.navigate(path, { replace: false });
      return true;
    } else {
      return false;
    }
  };

  navigateTo = (pageLink: string) => {
    if (pageLink.startsWith('#')) {
      pageLink = pageLink.substring(1);
    }
    if (Logger.isLogEnabled()) {
      Logger.log({
        itemType: 'navigateTo',
        status: 'debug',
        msg: `Navigating to ${pageLink}`,
      });
    }
    this.navigate(pageLink);
  };

  _getDataSourceAlias = (
    appUid: string,
    pageId: string,
    page: Page,
    itemId: ItemID,
    aliasType: AliasType,
  ): AliasAndEmbeddedPageIdValue | null => {
    let item = page[itemId];
    if (!item || item.type === 'Page') {
      return null;
    }
    const mgr = getManager(item.type);
    const prop = mgr.getDataSourceAliasProperty(aliasType, {
      mgr,
      appUid,
      pageId,
      itemId,
      platform: this,
    });
    if (
      aliasType === 'QUERY_FILTER' &&
      (item.type === 'ActionQueryChart' ||
        item.type === 'ActionQuerySelect' ||
        item.type === 'ActionQueryCalendar')
    ) {
      let aliasItemId = '';
      if (item.type === 'ActionQueryChart') {
        aliasItemId = (item as ActionQueryChartItem).props.chartItemId;
        if (isNotEmpty(aliasItemId)) item = page[aliasItemId];
      } else if (item.type === 'ActionQuerySelect') {
        aliasItemId = (item as ActionQuerySelectItem).props.selectItemId;
        if (isNotEmpty(aliasItemId)) item = page[aliasItemId];
      } else if (item.type === 'ActionQueryCalendar') {
        aliasItemId = (item as ActionQueryCalendarItem).props.calendarItemId;
        if (isNotEmpty(aliasItemId)) item = page[aliasItemId];
      }
    }

    // item might have got deleted, no need to analyze further
    if (!item || isNull(item)) {
      return null;
    }
    // @ts-ignore item.props
    if (prop && item.props[prop.aliasProp]) {
      return {
        // @ts-ignore item.props
        alias: item.props[prop.aliasProp],
        embeddedPageId: isNotEmpty(prop.embeddedPageIdProp)
          ? // @ts-ignore item.props
            item.props[prop.embeddedPageIdProp]
          : undefined,
      };
    }
    return this._getDataSourceAlias(
      appUid,
      pageId,
      page,
      item.parent,
      aliasType,
    );
  };

  getDataSourceAlias = (
    appUid: string,
    pageId: string,
    itemId: ItemID,
    aliasType: AliasType,
  ): AliasAndEmbeddedPageIdValue | null => {
    const page = this.getPage(appUid, pageId);
    return this._getDataSourceAlias(appUid, pageId, page, itemId, aliasType);
  };

  startAsyncTransition = async (
    cb: () => Promise<void>,
    setPending: (pending: boolean) => void,
    context?: { appUid: string; pageId: string; itemId: string },
  ): Promise<void> => {
    setPending(true);
    loadingSignal.loadingCount.set((v) => v + 1);
    return new Promise((resolve) => {
      this._startTransition();
      // console.log('startAsyncTransition.start');
      const fn = () => {
        let end = emptyFunction;
        if (context) {
          if (!context.appUid) {
            throw new Error('appUid is required for async transition');
          }
          startProgressTracking(this, context.appUid);
          end = startPageProgress(
            context.appUid,
            context.pageId,
            context.itemId,
          );
        }
        cb().finally(() => {
          this._endTransition();
          // console.log('startAsyncTransition.end');
          end();
          this.forceUpdater(emptyFunction);
          loadingSignal.loadingCount.set((v) => v - 1);
          setPending(false);
          resolve();
        });
      };
      let withTransition = true;
      if (context) {
        const page = this.getPage(context.appUid, context.pageId);
        /*
        When using the below actions inside the button click, the UI doesn’t reflect the changes
        until the trigger is completed, due to the use of startTransition.
        Hence skipping startTransition if any of these actions exist inside the trigger.
        Make sure to add any new actions that might need this behavior.
        One place where this is very visible is when using ActionRejectWorkflow
        inside a trigger; the dialog for user input doesn't show up until the startTransition
        times out as ActionRejectWorkflow doesn't allow the trigger promise to complete until
        the user input is provided.
        */
        withTransition = !this.hasChild(page, context.itemId, (item) =>
          (
            [
              'ActionClear',
              'ActionCloseDrawer',
              'ActionClosePopover',
              'ActionClosePopup',
              'ActionDeleteCurrentRecord',
              'ActionDeleteSelectedRecords',
              'ActionImport',
              'ActionOpenAttachmentRevisions',
              'ActionOpenComments',
              'ActionOpenDrawer',
              'ActionOpenPopover',
              'ActionOpenPopup',
              'ActionOpenSignaturePad',
              'ActionOpenUserProfile',
              'ActionRejectWorkflow',
              'ActionUserConfirmation',
            ] as ItemType[]
          ).includes(item.type),
        );
      }
      withTransition ? startTransition(fn) : fn();
    });
  };

  _startTransition: () => void = emptyFunction;

  _endTransition: () => void = emptyFunction;

  registerLoadingIndicator = (val: [() => void, () => void]) => {
    this._startTransition = val[0];
    this._endTransition = val[1];
  };

  hasPendingAtoms = (): boolean => {
    return (
      this.atomQueue.pending.length > 0 || this.atomQueue.waiting.length > 0
    );
  };

  postAtom = async (
    _atom: Omit<Atom, 'lastUpdateDate' | 'lastUpdatedBy' | 'id'>,
    {
      appUid,
      pageUid,
      roleUid,
    }: { appUid: string; pageUid?: string; roleUid?: string },
  ): Promise<boolean> => {
    const { ds, key } = _atom;
    const iatom: Omit<Atom, 'lastUpdateDate' | 'lastUpdatedBy'> = {
      ..._atom,
      id: `a${++this._atomCounter}`,
    };
    const isPending = this.atomQueue.pending.find(
      (p) => ds === p.ds && key === p.key,
    );
    if (isPending) {
      const notificationId = this.showWarning(
        'Previous update is still pending...!',
      );
      const promise = new Promise<boolean>((response, reject) => {
        // console.log('waiting', iatom);
        this.atomQueue.waiting.push([
          iatom,
          { appUid, pageUid, roleUid },
          response,
          reject,
          notificationId,
        ]);
      });
      return promise;
    }
    const lastUpdateDate = this.getter<string>([
      'db',
      ds,
      key,
      'lastUpdateDate',
    ]);
    const lastUpdatedBy = this.getter<string>(['db', ds, key, 'lastUpdatedBy']);
    const atom: Atom = {
      ...iatom,
      lastUpdateDate,
      lastUpdatedBy,
    };
    this.atomQueue.pending = [...this.atomQueue.pending, atom];
    let status = true;
    try {
      const r = await this.sendRequest(
        { type: 'PostAtom', atom },
        { appUid, roleUid, pageUid },
      )[0];
      if (r && r.status === 'success') {
        this.setter(['db', ds, key, 'lastUpdateDate'], r.data.lastUpdateDate);
        this.setter(['db', ds, key, 'lastUpdatedBy'], r.data.lastUpdatedBy);
        switch (ds) {
          case 'IoUserSettings':
            break;
          case 'IoAppSettings':
            this.showSuccess('Application settings saved successfully!');
            break;
          case 'IoPages':
            this.showSuccess('Page saved successfully!');
            break;
          default:
            this.showSuccess('Change posted successfully!');
        }
      }
    } catch (e) {
      // error is already shown to user in useWebSocket
      status = false;
    }
    this.atomQueue.pending = this.atomQueue.pending.filter(
      (_a) => _a.id !== atom.id,
    );

    const w = this.atomQueue.waiting.find(
      (a) => a[0].ds === ds && a[0].key === key,
    );
    if (w) {
      // console.log('processing from waiting', w[0]);
      this.atomQueue.waiting = this.atomQueue.waiting.filter(
        (_a) => _a[0].id !== w[0].id,
      );
      this.removeNotification(w[4]);
      this.postAtom(w[0], w[1]).then(w[2]).catch(w[3]);
    }
    return status;
  };

  isDeveloper = (appUid: string): boolean => {
    return isDeveloper(this, appUid);
  };

  invokeCloudFunction = async <I, O>(
    appUid: string,
    functionUri: string,
    data?: I,
  ): Promise<O> => {
    const user = this.getter(userAtom);
    const response = await fetch(
      `${getUrl()}fn/${appUid}/${functionUri}?${getURLQueryStringToPost(user)}`,
      {
        credentials: 'include',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-role-uid': this.roleUid,
        },
        body: stringify(data ?? {}),
      },
    );
    const json = await response.json();
    return json as O;
  };

  isRecording = (): boolean => {
    return this.getter(recordUIMessagesAtom) === this.appUid;
  };

  _lastRecordLineTime = 0;
  _indent = 0;
  recordLine = (itemId: string, message: string): void => {
    let comment = '';
    if (itemId) {
      const page = this.getPage(this.appUid, this.pageId);
      const item = page[itemId];
      const mgr = getManager(item.type);
      const shortId = mgr.generateShortId(item.props);
      comment = `// ${shortId ? `${shortId} ` : ''}${mgr.name}`;
    }

    if (message.startsWith('await ui.scope(')) {
      this._indent++;
    }
    if (message === '});') {
      this._indent--;
    }

    const now = performance.now();
    const recordWithWait = this.getter(recordUIMessagesWithWaitAtom);
    const waitLine =
      recordWithWait && this._lastRecordLineTime
        ? `await ui.wait(${Math.min(
            Math.floor(now - this._lastRecordLineTime),
            1200,
          )});\n`
        : '';

    if (message.startsWith('await ui.type(')) {
      this.setter(recordedUIMessagesAtom, (messages) => {
        const newMessages = [...messages];
        const startText = message.substring(0, message.indexOf(', ') + 2);
        if (
          newMessages.length &&
          newMessages[newMessages.length - 1].trimStart().startsWith(startText)
        ) {
          newMessages[newMessages.length - 1] = message;
        } else {
          if (newMessages.length > 1) {
            newMessages.push(' '.repeat(this._indent) + waitLine);
          }
          newMessages.push(' '.repeat(this._indent) + comment);
          newMessages.push(' '.repeat(this._indent) + message);
        }
        return newMessages;
      });
    } else {
      this.setter(recordedUIMessagesAtom, (messages) => [
        ...messages,
        messages.length > 1 ? ' '.repeat(this._indent) + waitLine : '',
        ' '.repeat(this._indent) + comment,
        ' '.repeat(this._indent) + message,
      ]);
    }
    this._lastRecordLineTime = performance.now();
  };
  getUserName = (): string => getUserName(this);
  find = makeFind(this);

  findOne = async <T extends DataSourceName>(
    appUid: string,
    ds: T,
    request: Query<AllDatasources[T]>,
  ): Promise<DBRow<AllDatasources[T]> | null> => {
    const rows = await this.find(appUid, ds, { ...request, limit: 1 });
    return rows.length ? rows[0] : null;
  };

  insertOne = makeSingleInsertPost(this, 'I');
  insertMany = makeMultiInsertPost(this, 'I');
  updateOne = makeSinglePost(this, 'U');
  updateMany = makeMultiPost(this, 'U');
  deleteOne = makeSinglePost(this, 'D');
  deleteMany = makeMultiPost(this, 'D');

  getURLParam = (param: string): string | null => getURLParam(param);
  getPageData = (appUid: string, pageId: ItemID, key: string) =>
    this.getter(['$pageData', appUid, pageId, key], undefined);
  setPageData = (
    appUid: string,
    pageId: ItemID,
    key: string,
    value: unknown,
  ) => {
    this.setter(['$pageData', appUid, pageId, key], value);
  };
  getGlobalData = (key: string) => this.getter(['$globalData', key], undefined);
  setGlobalData = (key: string, value: unknown) => {
    this.setter(['$globalData', key], value);
  };
  getLocalStorageData = (appUid: string, key: string) =>
    this.getter(localStorageSelector, {
      params: { appUid, key },
    });
  setLocalStorageData = (appUid: string, key: string, value: unknown) => {
    this.setter(localStorageSelector, value, { params: { appUid, key } });
  };
  setFocus = (itemId: string) => {
    this.setter(focusItemIdAtom, itemId);
  };
  invokeTrigger = async (
    appUid: string,
    pageId: string,
    targetItemId: string,
  ): Promise<boolean> => {
    return processChildActions({
      appUid,
      pageId,
      itemId: targetItemId,
      itemType: 'ActionInvokeControllerFunction',
      nestedActionCounter: 1,
      roleUid: '',
      platform: this,
      triggerItemId: '',
    });
  };
  clearAppConn = async (platform: Platform, appUid: string) => {
    const user = this.getter(userAtom);

    const r = await fetch(
      `${getUrl()}fn/${appUid}/clearCache?type=App&${getURLQueryStringToPost(
        user,
      )}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      },
    ).then((resp) => resp.json());

    if (r.status === 'OK') {
      platform.showSuccess('App connection cache cleared successfully!');
    } else {
      platform.showError(r.message);
    }
  };
}

export default IOPlatform;
